import { Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { DetailSection } from "@/components/shared/detail-section";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { LoadingRows } from "@/components/shared/loading-rows";
import { ReorderButtons } from "@/components/shared/reorder-buttons";
import { StatusPill } from "@/components/shared/status-badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  type ReviewCriterionRow,
  useDeleteReviewCriterion,
  useReorderReviewCriterion,
  useReviewCriteria,
  useUpdateReviewCriterion,
} from "@/hooks/use-review-criteria";
import { ReviewQuestionDialog } from "./ReviewQuestionDialog";

/**
 * The questions a review form can ask — Settings → Config → Reviews.
 *
 * A fifth section on the page that already holds the review reference lists,
 * rather than a thirteenth Config tab. It is richer than those four — a
 * question has help text, a group, an optional competency link and a place in
 * the standard set — so it has its own component rather than another
 * `ReviewConfigList`.
 */
export function ReviewQuestionsList() {
  const questions = useReviewCriteria();
  const update = useUpdateReviewCriterion();
  const remove = useDeleteReviewCriterion();
  const reorder = useReorderReviewCriterion();
  const [editing, setEditing] = useState<ReviewCriterionRow | null>(null);
  const [adding, setAdding] = useState(false);

  const rows = questions.data ?? [];
  const standardCount = rows.filter((r) => r.isDefault && r.isActive).length;

  return (
    <DetailSection
      title="Questions"
      headerActions={
        <Button size="sm" onClick={() => setAdding(true)}>
          <Plus className="size-4" />
          Add a question
        </Button>
      }
    >
      <p className="mb-4 max-w-3xl text-muted-foreground text-sm">
        What reviewers are asked. Mark the ones you use on most forms as standard and a new form
        starts with them already on it.{" "}
        {standardCount === 0
          ? "None are marked standard yet, so new forms start empty."
          : `${standardCount} ${standardCount === 1 ? "is" : "are"} marked standard.`}
      </p>

      {questions.isLoading ? (
        <LoadingRows />
      ) : questions.isError ? (
        <ErrorState
          message={
            questions.error instanceof Error ? questions.error.message : "Couldn't load questions"
          }
          onRetry={() => questions.refetch()}
        />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No questions yet"
          description="Add the first one, and it becomes available to every review form."
        />
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border">
          {rows.map((row, index) => (
            <li key={row.id} className="flex flex-wrap items-start gap-3 p-3">
              <ReorderButtons
                label={row.title}
                disableUp={index === 0 || reorder.isPending}
                disableDown={index === rows.length - 1 || reorder.isPending}
                onMove={(direction) => reorder.mutate({ id: row.id, direction })}
              />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{row.title}</span>
                  {row.isDefault && <StatusPill tone="info" label="Standard" />}
                  {row.categoryName && <StatusPill tone="neutral" label={row.categoryName} />}
                  {!row.isActive && <StatusPill tone="neutral" label="Switched off" />}
                </div>
                {row.helpText && (
                  <p className="mt-0.5 text-muted-foreground text-sm">{row.helpText}</p>
                )}
                <p className="mt-1 text-2xs text-muted-foreground">
                  {/*
                    Both facts are the ones that decide what an admin may do
                    next: whether reviewers get the role guidance, and whether
                    this can be deleted at all.
                  */}
                  {row.competencyName
                    ? `Shows role expectations from ${row.competencyName}`
                    : "No role expectations shown"}
                  {" · "}
                  {row.usedByForms === 0
                    ? "Not on any form"
                    : `On ${row.usedByForms} ${row.usedByForms === 1 ? "form" : "forms"}`}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {/* Same affordance the other review config lists use. */}
                <Label
                  htmlFor={`offered-${row.id}`}
                  className="flex cursor-pointer items-center gap-2 font-normal text-sm"
                >
                  <Checkbox
                    id={`offered-${row.id}`}
                    checked={row.isActive}
                    disabled={update.isPending}
                    onCheckedChange={(checked) =>
                      update.mutate({ id: row.id, isActive: checked === true })
                    }
                  />
                  Offered
                </Label>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={`Edit ${row.title}`}
                  onClick={() => setEditing(row)}
                >
                  <Pencil className="size-4" />
                </Button>
                {/*
                  Only offered where it is possible. A question a form uses is
                  refused by the service and by the database; showing the button
                  anyway would be a dead end with an explanation after the click.
                */}
                {row.usedByForms === 0 && (
                  <ConfirmDialog
                    trigger={
                      <Button variant="ghost" size="sm" aria-label={`Remove ${row.title}`}>
                        <Trash2 className="size-4" />
                      </Button>
                    }
                    title="Remove this question?"
                    description={`"${row.title}" isn't on any form, so removing it affects nothing that has been asked. If you might want it later, switch it off instead.`}
                    confirmLabel="Remove it"
                    onConfirm={() => remove.mutate(row.id)}
                  />
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      <ReviewQuestionDialog
        open={adding || editing !== null}
        question={editing}
        onOpenChange={(open) => {
          if (!open) {
            setAdding(false);
            setEditing(null);
          }
        }}
      />
    </DetailSection>
  );
}
